/*
 * Меню у стрелочек рядом с микрофоном и наушниками в панели пользователя: то, что нужно
 * посреди разговора, — устройство, обработка и громкость, — без захода в настройки.
 * Список устройств вложенной страницей, а не подменю: панель узкая, подменю некуда раскрыть.
 */
import { useEffect, useState } from 'react'
import { Room } from 'livekit-client'
import { Check, ChevronLeft, ChevronRight, Headphones, Mic, Settings } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useVoiceStore, type VoicePrefs } from '@/store/voice'
import { deviceLabel, selectableDevices, systemDefaultLabel } from '@/lib/devices'
import styles from './AudioMenu.module.css'

export type AudioMenuKind = 'input' | 'output'

/*
 * Профили обработки. Отдельного «Встроенного шумоподавления» браузера больше нет (26.09.2026): по проверке
 * оно ничего заметного не давало. Под RNNoise оно по-прежнему первым слоем — именно так RNNoise и выбрали.
 */
const PROFILES = [
  {
    id: 'isolation',
    label: 'RNNoise',
    hint: 'Шумоподавление с помощью нейросети',
    patch: { rnnoise: true, noiseSuppression: true },
  },
  {
    id: 'raw',
    label: 'Без обработки',
    hint: 'Звук как он есть',
    patch: { rnnoise: false, noiseSuppression: false },
  },
] as const

const profileOf = (p: VoicePrefs) => (p.rnnoise ? 'isolation' : 'raw')

export function AudioMenu({ kind, onClose }: { kind: AudioMenuKind; onClose: () => void }) {
  const prefs = useVoiceStore((s) => s.prefs)
  const setPrefs = useVoiceStore((s) => s.setPrefs)
  const openSettings = useAppStore((s) => s.openSettings)
  const [page, setPage] = useState<'root' | 'device' | 'profile'>('root')
  const [devices, setDevices] = useState<MediaDeviceInfo[] | null>(null)

  const isInput = kind === 'input'
  const deviceKind: MediaDeviceKind = isInput ? 'audioinput' : 'audiooutput'
  const prefKey = isInput ? 'audioinput' : 'audiooutput'
  const volumeKey = isInput ? 'inputVolume' : 'outputVolume'

  const load = async (permissions: boolean) => {
    if (permissions) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach((t) => t.stop())
      } catch {
        /* доступа не дали — покажем то, что есть */
      }
    }
    try {
      setDevices(await Room.getLocalDevices(deviceKind, false))
    } catch {
      setDevices([])
    }
  }
  useEffect(() => {
    void load(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKind])

  const all = devices ?? []
  const named = all.some((d) => d.label)
  // только то, что можно выбрать: без служебных «Default» и «Communications» Chrome (lib/devices.ts)
  const list = selectableDevices(all)
  const labelOf = deviceLabel
  const systemDefault = systemDefaultLabel(all)
  const current = list.find((d) => d.deviceId === prefs[prefKey])
  const currentLabel = !prefs[prefKey]
    ? 'По умолчанию'
    : current
      ? labelOf(current, list.indexOf(current))
      : 'Устройство недоступно'

  const volume = prefs[volumeKey]
  const percent = Math.round(volume * 100)

  if (page !== 'root') {
    const onDevice = page === 'device'
    const options = onDevice
      ? [{ id: '', label: 'По умолчанию', hint: systemDefault ? `Сейчас: ${systemDefault}` : (null as string | null) }, ...list.map((d, i) => ({ id: d.deviceId, label: labelOf(d, i), hint: null }))]
      : PROFILES.map((p) => ({ id: p.id, label: p.label, hint: p.hint }))
    const picked = onDevice ? prefs[prefKey] : profileOf(prefs)
    return (
      <div className={styles.menu} role="menu">
        <button type="button" className={styles.back} onClick={() => setPage('root')}>
          <ChevronLeft size={16} />
          {onDevice ? (isInput ? 'Устройство ввода' : 'Устройство вывода') : 'Профиль ввода'}
        </button>
        <div className={styles.divider} />
        <div className={styles.options}>
          {options.map((o) => (
            <button
              key={o.id || 'default'}
              type="button"
              role="menuitemradio"
              aria-checked={picked === o.id}
              className={styles.option}
              onClick={() => {
                if (onDevice) void setPrefs({ [prefKey]: o.id })
                else void setPrefs(PROFILES.find((p) => p.id === o.id)?.patch ?? {})
                setPage('root')
              }}
            >
              <span className={styles.optionMark}>{picked === o.id ? <Check size={14} /> : null}</span>
              <span className={styles.optionText}>
                <span className={styles.optionLabel}>{o.label}</span>
                {o.hint ? <span className={styles.optionHint}>{o.hint}</span> : null}
              </span>
            </button>
          ))}
        </div>
        {onDevice && devices && !named ? (
          <>
            <div className={styles.divider} />
            <button type="button" className={styles.item} onClick={() => void load(true)}>
              Показать названия устройств
            </button>
          </>
        ) : null}
      </div>
    )
  }

  return (
    <div className={styles.menu} role="menu">
      <button type="button" role="menuitem" className={styles.row} onClick={() => setPage('device')}>
        <span className={styles.rowText}>
          <span className={styles.rowLabel}>{isInput ? 'Устройство ввода' : 'Устройство вывода'}</span>
          <span className={styles.rowValue}>{currentLabel}</span>
        </span>
        <ChevronRight size={16} className={styles.rowChevron} />
      </button>
      {isInput ? (
        <button type="button" role="menuitem" className={styles.row} onClick={() => setPage('profile')}>
          <span className={styles.rowText}>
            <span className={styles.rowLabel}>Профиль ввода</span>
            <span className={styles.rowValue}>{PROFILES.find((p) => p.id === profileOf(prefs))?.label}</span>
          </span>
          <ChevronRight size={16} className={styles.rowChevron} />
        </button>
      ) : null}
      <div className={styles.divider} />
      <div className={styles.volume}>
        <div className={styles.volumeHead}>
          <span>{isInput ? <Mic size={14} /> : <Headphones size={14} />} {isInput ? 'Громкость микрофона' : 'Громкость звука'}</span>
          <span className={styles.volumeValue}>{percent}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={200}
          step={1}
          value={percent}
          aria-label={isInput ? 'Громкость микрофона' : 'Громкость звука'}
          onChange={(e) => void setPrefs({ [volumeKey]: Number(e.target.value) / 100 })}
        />
      </div>
      <div className={styles.divider} />
      <button
        type="button"
        role="menuitem"
        className={styles.item}
        onClick={() => {
          onClose()
          openSettings('voice')
        }}
      >
        <Settings size={16} /> Настройки голоса
      </button>
    </div>
  )
}
